
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { Tooltip } from '@mui/material';
import {
    Autocomplete as AbsAutocomplete,
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    FormOptionsContext,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FC, ReactElement, useContext } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ConditionContext, ConditionMode, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IGeneralConsentFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
{/* -- Hidden, as requested from 2025-01-22 changes */}
const hasPerformedClinicalReview = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.hasPerformedClinicalReview === Dtos.LookupYesNoType.Yes;

const ethnicityNotOther = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.ethnicityNotStatedUnknown !== true;

const countryOther = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.countryOther === true;
const countryNotOther = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.countryOther !== true;

const mainLanguageOther = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.languageSpokenAsChildOther === true;
const mainLanguageNotOther = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.languageSpokenAsChildOther !== true;

const mainLanguagePrincipleOther = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.languagePrincipleOther === true;
const mainLanguagePrincipleNotOther = ({ formState }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => formState?.value?.languagePrincipleOther !== true;

const australianSite = ({ country }: ICrfConditionParameters<Dtos.GeneralAndConsent>) => country?.name?.toLowerCase() === "australia";

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const FormTooltip: FC<{ title: string; children: ReactElement<unknown, any> }> = ({
    title,
    children
}) => {
    const { readOnly } = useContext(FormOptionsContext);

    const { conditionMet } = useContext(ConditionContext) ?? {};

    return (
        <Tooltip
            arrow
            open={!readOnly && (conditionMet === undefined || conditionMet)}
            placement="left"
            title={title}
        >
            {children}
        </Tooltip>
    )
}


const GeneralConsentForm: FC<IGeneralConsentFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings?.debug}
            >
                <GroupedField
                    propertyPath="isFemale"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="ageAtEntry"
                    component={Numeric}
                />
                <GroupedField
                    disabled
                    propertyPath="initials"
                    component={Text}
                />

                <GroupedFields>
                    <GroupedField
                        propertyPath="hasPerformedClinicalReview"
                        component={ToggleButtonGroup}
                    />
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={hasPerformedClinicalReview}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            propertyPath="performedClinicalReviewDate"
                            component={DatePicker}
                        />
                    </CrfCondition>
                </GroupedFields>

                <GroupedField
                    propertyPath="ecogStatus"
                    component={Select}
                />

                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={australianSite}
                    subscription={{ value: true }}
                >
                    <GroupedField
                        propertyPath="postcode"
                        component={Numeric}
                    />
                </CrfCondition>

                <GroupedFields>
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={ethnicityNotOther}
                        subscription={{ value: true }}
                    >
                        <FormTooltip
                            title="Type to search ethnicities"
                        >
                            <GroupedField
                                propertyPath="ethnicityId"
                                component={AbsAutocomplete}
                            />
                        </FormTooltip>
                    </CrfCondition>

                    <GroupedField
                        propertyPath="ethnicityNotStatedUnknown"
                        component={Checkbox}
                        label=""
                    />
                </GroupedFields>

                <GroupedFields>
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={countryNotOther}
                        subscription={{ value: true }}
                    >
                        <FormTooltip
                            title="Type to search countries"
                        >
                            <GroupedField
                                propertyPath="countryId"
                                component={AbsAutocomplete}
                            />
                        </FormTooltip>
                    </CrfCondition>
                    <GroupedField
                        propertyPath="countryOther"
                        component={Checkbox}
                        label=""
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={countryOther}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            propertyPath="countryOtherSpecify"
                            component={Text}
                        />
                    </CrfCondition>
                </GroupedFields>

                <GroupedFields>
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={mainLanguageNotOther}
                        subscription={{ value: true }}
                    >
                        <FormTooltip
                            title="Type to search language"
                        >
                            <GroupedField
                                propertyPath="languageSpokenAsChildId"
                                component={AbsAutocomplete}
                            />
                        </FormTooltip>
                    </CrfCondition>
                    <GroupedField
                        propertyPath="languageSpokenAsChildOther"
                        component={Checkbox}
                        label=""
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={mainLanguageOther}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            propertyPath="languageSpokenAsChildOtherSpecify"
                            component={Text}
                            inputProps={{ maxLength: 100 }}
                        />
                    </CrfCondition>
                </GroupedFields>

                <GroupedFields>
                    <CrfCondition
                        mode={ConditionMode.Enable}
                        condition={mainLanguagePrincipleNotOther}
                        subscription={{ value: true }}
                    >
                        <FormTooltip
                            title="Type to search language"
                        >
                            <GroupedField
                                propertyPath="languagePrinciple"
                                component={AbsAutocomplete}
                            />
                        </FormTooltip>
                    </CrfCondition>
                    <GroupedField
                        propertyPath="languagePrincipleOther"
                        component={Checkbox}
                        label=""
                    />
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={mainLanguagePrincipleOther}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            propertyPath="languagePrincipleOtherSpecify"
                            component={Text}
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100 
                                }
                            }}
                        />
                    </CrfCondition>
                </GroupedFields>
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default GeneralConsentForm;
